import { el, mount } from '../dom';

type Invite = { slot: number; url: string };

/**
 * The create-a-table screen at `/`.
 *
 * Not named in §12, but without it a table can only be created with curl — the
 * host passphrase gate has to be typed somewhere. Deliberately plain: it is used
 * once per evening by one person.
 *
 * The passphrase is held in a variable for the length of one request and never
 * stored. Putting it in localStorage would leave the key that guards table
 * creation sitting in the browser of whoever last hosted.
 */
export function renderHostPage(root: HTMLElement): void {
  const key = el('input', {
    type: 'password',
    class: 'input',
    id: 'host-key',
    placeholder: 'Host passphrase',
    autocomplete: 'current-password',
  });

  const status = el('p', { class: 'hint', role: 'status', 'aria-live': 'polite' });
  const results = el('div', { class: 'invites' });

  const create = el('button', {
    type: 'button',
    class: 'button button--primary',
    text: 'Create a table',
  });

  const submit = async (): Promise<void> => {
    const passphrase = key.value;
    if (passphrase.length === 0) {
      status.textContent = 'Enter the host passphrase first.';
      return;
    }

    create.setAttribute('disabled', '');
    status.textContent = 'Creating…';

    try {
      const response = await fetch('/api/tables', {
        method: 'POST',
        headers: { 'X-Host-Key': passphrase },
      });

      if (response.status === 403) {
        status.textContent = 'That passphrase was not accepted.';
        return;
      }
      if (!response.ok) {
        status.textContent = `Could not create a table (${response.status}).`;
        return;
      }

      const body = (await response.json()) as {
        tableId: string;
        invites: Invite[];
      };
      key.value = '';
      status.textContent =
        'Table created. Send one link per person — each link is a specific seat.';
      renderInvites(results, body.invites);
    } catch {
      status.textContent = 'Network error. Try again.';
    } finally {
      create.removeAttribute('disabled');
    }
  };

  create.addEventListener('click', () => void submit());
  key.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Enter') void submit();
  });

  mount(
    root,
    el('div', { class: 'page page--host' }, [
      el('h1', { text: "Liar's Dice" }),
      el('p', {
        class: 'hint',
        text: 'Create a table, then send one invite link to each player.',
      }),
      el('div', { class: 'field' }, [
        el('label', { class: 'field__label', for: 'host-key', text: 'Host passphrase' }),
        key,
      ]),
      create,
      status,
      results,
    ]),
  );
}

function renderInvites(container: HTMLElement, invites: Invite[]): void {
  const list = el('ol', { class: 'invites__list' });

  for (const invite of invites) {
    const field = el('input', {
      type: 'text',
      class: 'input input--mono',
      readonly: true,
      value: invite.url,
      'aria-label': `Invite link for seat ${invite.slot + 1}`,
    });

    const copy = el('button', {
      type: 'button',
      class: 'button button--small',
      text: 'Copy',
    });
    copy.addEventListener('click', () => {
      void navigator.clipboard.writeText(invite.url).then(
        () => {
          copy.textContent = 'Copied';
          setTimeout(() => (copy.textContent = 'Copy'), 1500);
        },
        () => {
          field.select();
          copy.textContent = 'Select + copy';
        },
      );
    });

    list.appendChild(
      el('li', { class: 'invites__item' }, [
        el('span', { class: 'invites__seat', text: `Seat ${invite.slot + 1}` }),
        field,
        copy,
      ]),
    );
  }

  mount(
    container,
    el('h2', { text: 'Invite links' }),
    el('p', {
      class: 'hint',
      text:
        'Each link claims one seat and works in one browser. They expire in 14 days.',
    }),
    list,
  );
}
